import { produce } from 'immer';

import {
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
  DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE,
  DOCUMENT_SETUP_CLEAR_ALL_STATE,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_CHANGE,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_DELETE,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTED,
  TEMPLATE_INFORMATION_FETCH_SUCCESS,
} from '@app/store/actionTypes';
import { ContractSetupAction } from '@app/store/contract-setup/actions';
import ContractSetupRequesterFieldType from '@app/store/contract-setup/RequesterFieldType';
import { DocumentSetupAction } from '@app/store/documentSetup/actions';
import RequesterImageField from '@app/store/documentSetup/RequesterImageField';
import RequesterFieldType from '@app/store/RequesterFieldType';
import {
  scheduledDocumentPreviewDataFetched,
  setupTemplateByDraft,
  TemplateAction,
} from '@app/store/template/actions';
import RequesterInputType from '@app/store/template/RequesterInputType';

type DocumentSetupRequesterImageFieldsState = RequesterImageField[];

const initialState: DocumentSetupRequesterImageFieldsState = [];

export default function reducer(
  state: DocumentSetupRequesterImageFieldsState = initialState,
  action: DocumentSetupAction | ContractSetupAction | TemplateAction
): DocumentSetupRequesterImageFieldsState {
  switch (action.type) {
    case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST:
    case CONTRACT_SETUP_BY_DRAFT_REQUEST:
    case DOCUMENT_SETUP_CLEAR_ALL_STATE:
      return initialState;
    case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { requesterFields } = action.payload;
        requesterFields.forEach((requesterField) => {
          const { id, positionX, positionY, positionZ, positionPage, sizeHeight, sizeWidth } =
            requesterField;
          if (requesterField.inputType === ContractSetupRequesterFieldType.IMAGE_FIELD) {
            draft.push({
              id,
              pageNumber: positionPage,
              type: 'image',
              values: {
                dataLabel: requesterField.dataLabel,
                fileId: null,
                fileOtp: null,
                fileUrl: requesterField.fileUrl,
                height: sizeHeight,
                imageSrc: null,
                width: sizeWidth,
                x: positionX,
                y: positionY,
                z: positionZ,
              },
            });
          }
        });
      });
    case DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_CHANGE:
      return produce(state, (draft) => {
        const { id, values } = action.payload;
        const index = draft.findIndex((v) => v.id === id);
        draft[index].values = values;
      });
    case DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_DELETE:
      return produce(state, (draft) => {
        const { id } = action.payload;
        const index = state.findIndex((v) => v.id === id);
        draft.splice(index, 1);
      });
    case DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTED:
      return produce(state, (draft) => {
        const { id, pageNumber, values } = action.payload;
        draft.push({
          id,
          pageNumber,
          type: 'image',
          values,
        });
      });
    case scheduledDocumentPreviewDataFetched.type:
    case TEMPLATE_INFORMATION_FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { requesterInputs } = action.payload;
        requesterInputs.forEach((requesterField) => {
          if (requesterField.fieldType === RequesterInputType.IMAGE) {
            const { dataLabel, file, id, position, size } = requesterField;
            draft.push({
              id,
              pageNumber: position.page,
              type: 'image',
              values: {
                dataLabel,
                fileId: null,
                fileOtp: null,
                fileUrl: file?.url ?? null,
                height: size.height,
                imageSrc: null,
                width: size.width,
                x: position.x,
                y: position.y,
                z: position.z,
              },
            });
          }
        });
      });
    case CONTRACT_SETUP_BY_DRAFT_SUCCESS:
    case setupTemplateByDraft.type:
      return produce(state, (draft) => {
        action.payload.requesterInputs.forEach((field, index) => {
          if (field.type === RequesterFieldType.IMAGE) {
            draft.push({
              id: field.id,
              pageNumber: field.position.page,
              type: 'image',
              values: {
                dataLabel: field.dataLabel,
                fileId: null,
                fileOtp: null,
                fileUrl: field.value,
                height: field.size.height,
                imageSrc: null,
                width: field.size.width,
                x: field.position.x,
                y: field.position.y,
                z: field.position.z ?? index + 1,
              },
            });
          }
        });
      });
    case DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE:
      return produce(state, (draft) => {
        const { maxPage } = action.payload;
        return draft.filter((field) => field.pageNumber <= maxPage);
      });
    default:
      return state;
  }
}
